import { observer } from "mobx-react-lite";
import * as React from "react";

export type StopSaving = (status?: "success" | "error") => void;

interface ContextValue {
  content: [
    HTMLElement | null,
    React.Dispatch<React.SetStateAction<HTMLElement | null>>,
  ];
  saving: Saving;
  startSaving: () => StopSaving;
}

interface Saving {
  status: "hidden" | "loading" | "success" | "error";
}

export const AuthenticatedNavigationContext =
  React.createContext<ContextValue | null>(null);

export const AuthenticatedNavigationContextProvider = observer(
  function AuthenticatedNavigationContextProvider(
    props: React.PropsWithChildren,
  ) {
    const content = React.useState<HTMLElement | null>(null);
    const [saving, setSaving] = React.useState<Saving>({
      status: "hidden",
    });
    const latestStartCallIdentifier = React.useRef({});
    const hideSaveSuccessTimeoutRef = React.useRef(0);
    const startSuccessTimeoutRef = React.useRef(0);

    function startSaving() {
      const startTime = Date.now();

      const currentIdentifier = {};
      latestStartCallIdentifier.current = currentIdentifier;

      clearTimeout(hideSaveSuccessTimeoutRef.current);
      clearTimeout(startSuccessTimeoutRef.current);

      setSaving({
        status: "loading",
      });

      return (status: "success" | "error" = "success") => {
        if (latestStartCallIdentifier.current !== currentIdentifier) {
          return;
        }

        // Hey Matt, you might be wondering WTF is that?
        // This is just for better UX - we wait at least 300ms (in case API was very fast like 20ms :angel:)
        // Thanks and have a wonderful day!
        const elapsed = Date.now() - startTime;
        const delay = Math.max(0, 300 - elapsed);

        startSuccessTimeoutRef.current = window.setTimeout(() => {
          if (latestStartCallIdentifier.current !== currentIdentifier) {
            return;
          }

          setSaving({
            status,
          });
        }, delay);

        hideSaveSuccessTimeoutRef.current = window.setTimeout(() => {
          if (latestStartCallIdentifier.current !== currentIdentifier) {
            return;
          }
          setSaving({
            status: "hidden",
          });
        }, delay + 2700);
      };
    }

    const unperformantFreak = {
      content,
      saving,
      startSaving,
    };

    return (
      <AuthenticatedNavigationContext.Provider value={unperformantFreak}>
        {props.children}
      </AuthenticatedNavigationContext.Provider>
    );
  },
);

export function useAuthenticatedNavigationContext() {
  const context = React.useContext(AuthenticatedNavigationContext);

  if (!context) {
    throw new Error(
      "useAuthenticatedNavigationContext must be used within a AuthenticatedNavigationContextProvider",
    );
  }

  return context;
}

export function useSetContentElement() {
  const { content } = useAuthenticatedNavigationContext();

  return content[1];
}

export function useContentElement() {
  const { content } = useAuthenticatedNavigationContext();

  return content[0];
}
